<template>
  <!-- 分享弹窗 -->
  <BasePopup
    v-model:visible="visible"
    popup-class="share-popup"
    :mask-closable="true"
    :show-close-btn="true"
    :show-confirm-btn="false"
  >
    <!-- 提示文案 -->
    <div class="share-tips-wrapper">
      <span class="share-tips-date">{{ shareTipsDate }}</span>
      <span class="share-tips-text">{{ shareTips }}</span>
    </div>

    <!-- 邀请码（仅队伍邀请时展示且邀请码不为空） -->
    <div v-if="showInviteCode" class="share-link-row">
      <div class="share-link-label">邀請碼</div>
      <div class="share-link-url">{{ myCode }}</div>
    </div>

    <!-- 分享链接 -->
    <div class="share-link-row">
      <div class="share-link-label">{{ linkLabel }}</div>
      <div class="share-link-url">{{ displayUrl }}</div>
    </div>

    <!-- 底部操作区 -->
    <div class="share-actions">
      <!-- 左侧：社媒图标 -->
      <div class="share-social-icons">
        <div class="share-icon-fb" @click="shareTo('facebook')"></div>
        <div class="share-icon-line" @click="shareTo('line')"></div>
      </div>

      <!-- 右侧：复制按钮 -->
      <div class="share-copy-btn" @click="copyLink"></div>
    </div>
  </BasePopup>
</template>

<script setup>
import { computed, inject, ref, watch } from 'vue';
import { point } from "@/utils";
import BasePopup from './BasePopup.vue';
import { getJSON } from '@/api/request';
import { getActivityApi } from '@/utils/index';
import { getShortLink } from '@/hooks/shareHook';

// Props
const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  mode: {
    type: String,
    default: 'daily', // 'daily': 普通分享, 'team_invite': 组队邀请
    validator: (value) => ['daily', 'team_invite'].includes(value)
  }
});

// Emits
const emit = defineEmits(['update:modelValue']);

// Computed
const visible = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
});

// 注入分享相关方法和数据
const initShare = inject('initShare');
const getShareSet = inject('getShareSet');
const updateShareLink = inject('updateShareLink');
const showTipMessage = inject('showTipMessage', () => {});
const initActivity = inject('initActivity', () => {});
const myCode = inject('myCode', ref('')); // 注入邀请码
const roleName = inject('roleName', ref('')); // 注入角色名

// 是否显示邀请码（仅组队邀请模式且有邀请码时显示）
const showInviteCode = computed(() => props.mode === 'team_invite' && !!myCode.value);

// 短链地址（优先使用短链，否则使用原始链接）
const finalShareUrl = ref('');

// 获取当前分享链接（原始链接，用于降级）
const shareUrl = computed(() => {
  const baseUrl = window.location.origin + window.location.pathname;

  // 组队邀请模式：带 roleName 和 inviteCode 参数
  if (props.mode === 'team_invite' && myCode.value) {
    return `${baseUrl}?roleName=${encodeURIComponent(roleName.value)}&inviteCode=${myCode.value}`;
  }

  // 普通分享模式：不带参数
  return baseUrl;
});

// 最终展示的链接（优先使用短链，否则使用原始链接）
const displayUrl = computed(() => finalShareUrl.value || shareUrl.value);

// 弹窗文案（根据模式动态调整）
const shareTipsDate = computed(() => '3月26日新版本重磅開啟！');
const shareTips = computed(() => '和我一起組隊拿積分，抽取驚喜好禮！');

// 链接标签文案（组队是"邀请链接"，分享是"分享链接"）
const linkLabel = computed(() => props.mode === 'team_invite' ? '邀請鏈接' : '分享鏈接');

// 复制文案内容
const copyText = computed(() => {
  let text = `${shareTipsDate.value}${shareTips.value}\n`;

  // 组队邀请模式：包含邀请码
  if (props.mode === 'team_invite' && myCode.value) {
    text += `邀請碼：${myCode.value}\n`;
  }

  // 添加链接
  text += `${linkLabel.value}：${displayUrl.value}`;

  return text;
});

// 监听弹窗打开，初始化分享并更新分享链接（生成短链）
watch(() => props.modelValue, async (newVal) => {
  if (newVal) {
    // 打点上报：分享弹窗展示
    point({
      eventName: 'show_popup',
      eventType: props.mode === 'team_invite' ? '组队邀请分享弹窗' : '普通分享弹窗'
    });

    try {
      // 构建分享参数对象（参考 template_116）
      const shareParams = {};

      if (props.mode === 'team_invite' && myCode.value) {
        // 组队邀请模式：传递邀请码和角色名
        shareParams.inviteCode = myCode.value;
        shareParams.roleName = encodeURIComponent(roleName.value);
      }

      // 生成短链（使用 globalGetShareLink + getPlatformShortLink）
      const platform = props.mode === 'team_invite' ? 'team_invite' : 'daily';
      const finalLink = await getShortLink(platform, shareParams);

      // 保存短链用于展示和复制
      finalShareUrl.value = finalLink;

      // 初始化分享（第一次会创建实例，后续只更新链接）
      if (initShare) {
        initShare(finalLink);
      }

      console.log(`✅ 分享链接已更新 (${props.mode}):`, finalLink);
    } catch (error) {
      console.error('❌ 更新分享链接失败:', error);
      // 失败时使用原始链接
      finalShareUrl.value = shareUrl.value;
      if (initShare) {
        initShare(shareUrl.value);
      }
    }
  }
});

// 分享方法
const shareTo = (platform) => {
  try {
    const shareSet = getShareSet?.();
    if (!shareSet) {
      console.error('shareSet 未初始化');
      return;
    }

    const shareInst = shareSet[platform];
    if (shareInst) {
      // 调用分享实例的 share 方法
      // 打点将在 ShareFactory 的 callback 中统一处理
      shareInst.share();

      // 分享成功后关闭弹窗
      setTimeout(() => {
        visible.value = false;
      }, 300);
    } else {
      console.error(`分享实例 ${platform} 不存在`);
    }
  } catch (error) {
    console.error('分享失败:', error);
  }
};

// 复制链接
const copyLink = async () => {
  // callbackType 固定为 'copy'（复制操作）
  const callbackType = 'copy';

  try {
    await navigator.clipboard.writeText(copyText.value);
    showTipMessage('連結已複製');

    // 打点上报：复制链接成功
    point({
      eventName: 'share_result',
      eventType: '复制链接',
      eventReason: '分享成功',
      eventValue: {}
    });

    // 调用 completeCallback 接口
    try {
      const url = getActivityApi(window.platform_id, window.activity_name, 'completeCallback');
      const res = await getJSON(url, {
        params: JSON.stringify({
          shareRes: 1,
          shareType: callbackType,
          callbackType: callbackType,
          shareMsg: ''
        })
      });
      console.log('✅ 复制链接回调成功');

      // 刷新活动数据
      if (res.result === 1) {
        initActivity();
      }
    } catch (error) {
      console.error('❌ 复制链接回调接口调用失败:', error);
    }
  } catch (error) {
    console.error('复制失败:', error);

    // 降级方案
    const textArea = document.createElement('textarea');
    textArea.value = copyText.value;
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand('copy');
      showTipMessage('連結已複製');

      // 打点上报：复制链接成功（降级方案）
      point({
        eventName: 'share_result',
        eventType: '复制链接',
        eventReason: '分享成功',
        eventValue: {}
      });

      // 调用 completeCallback 接口
      try {
        const url = getActivityApi(window.platform_id, window.activity_name, 'completeCallback');
        const res = await getJSON(url, {
          params: JSON.stringify({
            shareRes: 1,
            shareType: callbackType,
            callbackType: callbackType,
            shareMsg: ''
          })
        });
        console.log('✅ 复制链接回调成功');

        // 刷新活动数据
        if (res.result === 1) {
          initActivity();
        }
      } catch (err) {
        console.error('❌ 复制链接回调接口调用失败:', err);
      }
    } catch (err) {
      console.error('降级复制也失败:', err);
      // 打点上报：复制链接失败
      point({
        eventName: 'share_result',
        eventType: '复制链接',
        eventReason: '分享失败',
        eventValue: { error: err.message || String(err) }
      });
    }
    document.body.removeChild(textArea);
  }
};
</script>

<style scoped lang="less">
</style>
